import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    Container,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    FormGroup,
    Label,
    Collapse,
    Badge
} from "reactstrap";
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { Link } from "react-router-dom";

export default function Allcardetails() {
    const API_URL = process.env.REACT_APP_APIURL;

    const breadcrumbItems = [
        { title: "HOME", link: "/" },
        { title: "All Specifications", link: "#" },
    ];

    const [specifications, setSpecifications] = useState([]);
    const [categories, setCategories] = useState([]); // Store all categories
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSpec, setExpandedSpec] = useState(null);

    // Delete modal state
    const [deleteId, setDeleteId] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editField, setEditField] = useState("");
    const [editValue, setEditValue] = useState("");
    const [editCategory, setEditCategory] = useState("");
    const [editOptions, setEditOptions] = useState([]); // For category dropdown

    // Fetch both specifications and categories
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch specifications
                const specResponse = await fetch(`${API_URL}/carSpecification`);
                if (!specResponse.ok) throw new Error(`HTTP error! status: ${specResponse.status}`);
                const specData = await specResponse.json();
                setSpecifications(specData.getSpecification || []);

                // Fetch categories
                const catResponse = await fetch(`${API_URL}/category`);
                if (!catResponse.ok) throw new Error(`HTTP error! status: ${catResponse.status}`);
                const catData = await catResponse.json();
                setCategories(catData.allCategory || []);

            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to fetch data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [API_URL]);

    // Toggle expansion of specification details
    const toggleExpand = (id) => {
        setExpandedSpec(expandedSpec === id ? null : id);
    };

    // Delete
    const confirmDelete = (id) => {
        setDeleteId(id);
        setDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(`${API_URL}/carSpecification/${deleteId}`, {
                method: "DELETE"
            });

            if (!response.ok) throw new Error("Failed to delete specification");

            setSpecifications((prev) => prev.filter((item) => item._id !== deleteId));
            setDeleteModalOpen(false);
            setDeleteId(null);
        } catch (err) {
            console.error("Delete error:", err);
            alert("Error deleting specification.");
        }
    };

    const handleCancelDelete = () => {
        setDeleteId(null);
        setDeleteModalOpen(false);
    };

    // Get category name by ID
    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat._id === categoryId);
        return category ? category.category : 'Unknown Category';
    };

    // Edit - Open modal with current values
    const openEditModal = (id, category, field, currentValue) => {
        setEditId(id);
        setEditCategory(category);
        setEditField(field);
        setEditValue(currentValue);

        // If editing category, prepare dropdown options
        if (category === 'category') {
            setEditOptions(categories);
        } else {
            setEditOptions([]);
        }

        setEditModalOpen(true);
    };

    const handleEditSubmit = async () => {
        try {
            let updatePayload;

            if (editCategory === 'root') {
                // Root-level fields like carName, carPrice, carModal
                updatePayload = { [editField]: editValue };
            } else if (editCategory === 'category') {
                updatePayload = { [editField]: editValue };
            } else {
                // Nested fields
                updatePayload = {
                    [editCategory]: {
                        [editField]: editValue
                    }
                };
            }

            const response = await fetch(`${API_URL}/carSpecification/${editId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatePayload),
            });

            if (!response.ok) throw new Error("Failed to update specification");

            // Update local state
            setSpecifications(prev =>
                prev.map(item =>
                    item._id === editId
                        ? editCategory === 'root'
                            ? { ...item, [editField]: editValue }
                            : editCategory === 'category'
                                ? { ...item, [editField]: editValue }
                                : {
                                    ...item,
                                    [editCategory]: {
                                        ...item[editCategory],
                                        [editField]: editValue
                                    }
                                }
                        : item
                )
            );

            setEditModalOpen(false);
            resetEditState();
        } catch (err) {
            console.error("Edit error:", err);
            alert("Error updating specification.");
        }
    };


    const resetEditState = () => {
        setEditId(null);
        setEditCategory("");
        setEditField("");
        setEditValue("");
        setEditOptions([]);
    };

    const handleCancelEdit = () => {
        setEditModalOpen(false);
        resetEditState();
    };

    // Render nested object as editable fields
    const renderNestedFields = (data, category, id) => {
        if (!data) return null;

        return Object.entries(data).map(([field, value]) => (
            <tr key={`${category}-${field}`}>
                <td>{field}</td>
                <td>
                    {typeof value === 'boolean' ?
                        (value ? <Badge color="success">Yes</Badge> : <Badge color="danger">No</Badge>) :
                        String(value)}
                </td>
                <td>
                    <button
                        className="btn btn-sm btn-light"
                        onClick={() => openEditModal(id, category, field, value)}
                    >
                        <i className="mdi mdi-pencil text-primary font-size-18"></i>
                    </button>
                </td>
            </tr>
        ));
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title="All Specifications"
                        breadcrumbItems={breadcrumbItems}
                    />
                    <Card>
                        <CardBody>
                            <div className="table-responsive">
                                <table className="table table-centered align-middle table-nowrap mb-0 table-bordered">
                                    <thead className="table-light text-muted">
                                        <tr>
                                            <th>ID</th>
                                            <th>Category</th>
                                            <th>Car Name</th>
                                            <th>Price</th>
                                            <th>Modal</th>
                                            <th>Color</th>
                                            <th>Owner</th>
                                            <th>Insurance Validity</th>
                                            <th>Insurance Type</th>
                                            <th>No Claim Bonus</th>
                                            <th>Registration Number</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="6" className="text-center">Loading...</td>
                                            </tr>
                                        ) : error ? (
                                            <tr>
                                                <td colSpan="6" className="text-center text-danger">{error}</td>
                                            </tr>
                                        ) : specifications.length > 0 ? (
                                            specifications.map((spec, index) => (
                                                <React.Fragment key={spec._id}>
                                                    <tr>
                                                        <td>{index + 1}</td>
                                                        {/* Category */}
                                                        <td>
                                                            {getCategoryName(spec.category)}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'category',
                                                                    'category',
                                                                    spec.category
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>

                                                        {/* Car Name */}
                                                        <td>
                                                            {spec.carName || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'carName',
                                                                    spec.carName
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>

                                                        {/* Car Price */}
                                                        <td>
                                                            {spec.carPrice || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'carPrice',
                                                                    spec.carPrice
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>

                                                        {/* Car Modal */}
                                                        <td>
                                                            {spec.carModal || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'carModal',
                                                                    spec.carModal
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>
                                                             <td>
                                                            {spec.carOverView?.color || 'N/A'}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'carOverView',
                                                                    'color',
                                                                    spec.carOverView?.color
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>
                                                        <td>
                                                            {spec.ownerSerial || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'ownerSerial',
                                                                    spec.ownerSerial
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>
                                                         <td>
                                                            {spec.insuranceValidity || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'insuranceValidity',
                                                                    spec.insuranceValidity
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>

                                                         <td>
                                                            {spec.insuranceType || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'insuranceType',
                                                                    spec.insuranceType
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>

                                                        
                                                         <td>
                                                            {spec.noClaimBonus || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'noClaimBonus',
                                                                    spec.noClaimBonus
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>

                                                        <td>
                                                            {spec.registrationNumber || "N/A"}
                                                            <button
                                                                className="btn btn-sm btn-light ms-2"
                                                                onClick={() => openEditModal(
                                                                    spec._id,
                                                                    'root',
                                                                    'registrationNumber',
                                                                    spec.registrationNumber
                                                                )}
                                                            >
                                                                <i className="mdi mdi-pencil text-primary font-size-14"></i>
                                                            </button>
                                                        </td>
                                                        {/* Actions */}
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-light me-2"
                                                                onClick={() => toggleExpand(spec._id)}
                                                            >
                                                                <i className={`mdi mdi-chevron-${expandedSpec === spec._id ? 'up' : 'down'} font-size-18`}></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-light"
                                                                onClick={() => confirmDelete(spec._id)}
                                                            >
                                                                <i className="mdi mdi-trash-can text-danger font-size-18"></i>
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {/* Expanded Section */}
                                                    {expandedSpec === spec._id && (
                                                        <>
                                                            {/* Car Overview */}
                                                            <tr className="bg-light">
                                                                <td colSpan="6" className="font-weight-bold">Car Overview</td>
                                                            </tr>
                                                            {renderNestedFields(spec.carOverView, 'carOverView', spec._id)}

                                                            {/* Safety Features */}
                                                            <tr className="bg-light">
                                                                <td colSpan="6" className="font-weight-bold">Safety Features</td>
                                                            </tr>
                                                            {renderNestedFields(spec.safety, 'safety', spec._id)}

                                                            {/* Exterior Features */}
                                                            <tr className="bg-light">
                                                                <td colSpan="6" className="font-weight-bold">Exterior Features</td>
                                                            </tr>
                                                            {renderNestedFields(spec.exterior, 'exterior', spec._id)}

                                                            {/* Basic Features */}
                                                            <tr className="bg-light">
                                                                <td colSpan="6" className="font-weight-bold">Basic Features</td>
                                                            </tr>
                                                            {renderNestedFields(spec.basic, 'basic', spec._id)}
                                                        </>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center">No specifications found.</td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>
                            </div>
                        </CardBody>
                    </Card>
                </Container>

                {/* Delete Modal */}
                <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete}>
                    <ModalHeader toggle={handleCancelDelete}>Confirm Deletion</ModalHeader>
                    <ModalBody>Are you sure you want to delete this specification?</ModalBody>
                    <ModalFooter>
                        <Button color="danger" onClick={handleDelete}>
                            Yes, Delete
                        </Button>
                        <Button color="secondary" onClick={handleCancelDelete}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Edit Modal - Dynamic based on field type */}
                <Modal isOpen={editModalOpen} toggle={handleCancelEdit}>
                    <ModalHeader toggle={handleCancelEdit}>
                        Edit {editField} ({editCategory})
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="editInput">{editField}</Label>
                            {editOptions.length > 0 ? (
                                // Category dropdown
                                <Input
                                    type="select"
                                    id="editInput"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                >
                                    {editOptions.map(option => (
                                        <option key={option._id} value={option._id}>
                                            {option.category}
                                        </option>
                                    ))}
                                </Input>
                            ) : typeof editValue === 'boolean' ? (
                                // Boolean field
                                <Input
                                    type="select"
                                    id="editInput"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value === 'true')}
                                >
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Input>
                            ) : (
                                // Text/number field
                                <Input
                                    type={typeof editValue === 'number' ? 'number' : 'text'}
                                    id="editInput"
                                    value={editValue}
                                    onChange={(e) => setEditValue(
                                        typeof editValue === 'number' ?
                                            Number(e.target.value) :
                                            e.target.value
                                    )}
                                />
                            )}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleEditSubmit}>
                            Save Changes
                        </Button>
                        <Button color="secondary" onClick={handleCancelEdit}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        </React.Fragment>
    );
}